import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { ist_wall_clock_to_epoch, utc_label } from './ist_time';

/**
 * The slot time input, hard-wired to Asia/Kolkata. See ist_time.js for why the zone
 * is a constant rather than the header dropdown. The UTC line below the field stays
 * as the guard against a mistyped time.
 */

// A native time input cannot be limited to quarter hours: Chrome's picker ignores
// `step` and still lists all sixty minutes. Hence a select of the 96 valid times.
const QUARTER_HOURS = Array.from({ length: 96 }, (_, i) => {
  const hour = Math.floor(i / 4);
  const minute = (i % 4) * 15;
  const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return { value, label: moment(value, 'HH:mm').format('h:mm A') };
});

// A slot stored before this constraint stays selectable, rather than being snapped
// to a time the host never chose.
const options_for = (value) =>
  !value || QUARTER_HOURS.some((option) => option.value === value)
    ? QUARTER_HOURS
    : [{ value, label: `${moment(value, 'HH:mm').format('h:mm A')} (off-grid)` }, ...QUARTER_HOURS];

export default function ISTTimeInput({ run_date, value, onChange, disabled, invalid }) {
  const epoch = value ? ist_wall_clock_to_epoch(run_date, value) : null;

  return (
    <div>
      <select
        value={value || ''}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Start time (IST)"
        className="px-2 py-1 rounded text-sm"
        style={{
          width: 130,
          background: disabled ? 'var(--surface-sunken)' : 'var(--input-bg)',
          border: `1px solid ${invalid ? 'var(--state-error)' : 'var(--input-border)'}`,
          color: 'var(--input-text)'
        }}
      >
        <option value="">Pick a time</option>
        {options_for(value).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {epoch !== null && (
        <div className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
          = {utc_label(epoch)} UTC
        </div>
      )}
    </div>
  );
}

ISTTimeInput.propTypes = {
  run_date: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool
};

ISTTimeInput.defaultProps = {
  value: '',
  disabled: false,
  invalid: false
};
